import type { JSX } from 'react';
import type { BillingAddressBody, ClientResource } from '@majuscule/contracts';
import type { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

/**
 * Billing details of a client, written to Qonto, which holds them (D-QONTO.8).
 * The SIREN is required (it is also the e-invoicing address); the address is
 * all or nothing, and without it the monthly close cannot issue the invoice.
 */
export const billingFieldsSchema = z
  .object({
    legalName: z.string().trim().min(1, 'Obligatoire').max(200),
    siren: z
      .string()
      .trim()
      .regex(/^\d{9}$/, '9 chiffres'),
    vatNumber: z.string().trim().max(20),
    billingEmail: z.string().trim().email('Email invalide'),
    street: z.string().trim().max(250),
    zipCode: z.string().trim().max(20),
    city: z.string().trim().max(50),
    countryCode: z
      .string()
      .trim()
      .regex(/^[A-Z]{2}$/, 'Code ISO à 2 lettres, ex. FR'),
  })
  .refine(
    (v) =>
      [v.street, v.zipCode, v.city].every((f) => f !== '') ||
      [v.street, v.zipCode, v.city].every((f) => f === ''),
    { message: 'Adresse complète requise : rue, code postal et ville', path: ['street'] },
  );
export type BillingFieldsValues = z.infer<typeof billingFieldsSchema>;

/** Form values from a client (or empty ones for a new client). */
export function billingValuesOf(client: ClientResource | null): BillingFieldsValues {
  return {
    legalName: client?.legalName ?? '',
    siren: client?.siren ?? '',
    vatNumber: client?.vatNumber ?? '',
    billingEmail: client?.billingEmail ?? '',
    street: client?.billingAddress?.street ?? '',
    zipCode: client?.billingAddress?.zipCode ?? '',
    city: client?.billingAddress?.city ?? '',
    countryCode: client?.billingAddress?.countryCode ?? 'FR',
  };
}

/** The API body of the billing details. */
export function billingPayload(values: BillingFieldsValues): {
  legalName: string;
  siren: string;
  vatNumber: string | null;
  billingEmail: string;
  billingAddress: BillingAddressBody | null;
} {
  return {
    legalName: values.legalName,
    siren: values.siren,
    vatNumber: values.vatNumber === '' ? null : values.vatNumber,
    billingEmail: values.billingEmail,
    billingAddress:
      values.street === ''
        ? null
        : {
            street: values.street,
            zipCode: values.zipCode,
            city: values.city,
            countryCode: values.countryCode,
          },
  };
}

/** One-line address, « 12 rue de la Paix, 75002 Paris (FR) ». */
export function formatAddress(address: BillingAddressBody | null): string {
  return address === null
    ? 'Adresse non renseignée'
    : `${address.street}, ${address.zipCode} ${address.city} (${address.countryCode})`;
}

/** The billing fields, on a 4-column grid. */
export function BillingFields({ form }: { form: UseFormReturn<BillingFieldsValues> }): JSX.Element {
  const errors = form.formState.errors;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
      <Field
        label="Raison sociale"
        required
        className="md:col-span-2"
        error={errors.legalName?.message}
      >
        <Input placeholder="Acme SAS" {...form.register('legalName')} />
      </Field>
      <Field
        label="Email de facturation"
        required
        className="md:col-span-2"
        error={errors.billingEmail?.message}
      >
        <Input placeholder="compta@acme.fr" {...form.register('billingEmail')} />
      </Field>
      <Field label="SIREN" required className="md:col-span-2" error={errors.siren?.message}>
        <Input className="font-mono" placeholder="123456789" {...form.register('siren')} />
      </Field>
      <Field label="N° de TVA" className="md:col-span-2" error={errors.vatNumber?.message}>
        <Input className="font-mono" placeholder="FR12123456789" {...form.register('vatNumber')} />
      </Field>
      <Field label="Adresse" className="md:col-span-4" error={errors.street?.message}>
        <Input placeholder="12 rue de la Paix" {...form.register('street')} />
      </Field>
      <Field label="Code postal" error={errors.zipCode?.message}>
        <Input placeholder="75002" {...form.register('zipCode')} />
      </Field>
      <Field label="Ville" className="md:col-span-2" error={errors.city?.message}>
        <Input placeholder="Paris" {...form.register('city')} />
      </Field>
      <Field label="Pays" error={errors.countryCode?.message}>
        <Input className="font-mono" placeholder="FR" {...form.register('countryCode')} />
      </Field>
    </div>
  );
}
